'use client'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SIGNATURE_FONTS, signatureFontStack } from '@/constant/signature-fonts'
import { SIGNATURE_PLACEHOLDER_CLASS, SIGNATURE_SURFACE_CLASS } from '@/constant/signature-surface'
import { cn } from '@/lib/utils'
import { useEffect, useState } from 'react'

export interface SignatureTypedProps {
  value: string
  font: string
  onChange: (value: { typedText: string; typedFont: string }) => void
  disabled?: boolean
  className?: string
  placeholder?: string
}

/**
 * Typed signature capture.
 *
 * Always offered alongside drawing: a canvas is unusable with a keyboard or a
 * screen reader, so this is the accessible path to signing, not a lesser option.
 */
const SignatureTyped = ({
  value,
  font,
  onChange,
  disabled = false,
  className,
  placeholder = 'Type your full name',
}: SignatureTypedProps) => {
  const [selectedFont, setSelectedFont] = useState(font || SIGNATURE_FONTS[0].id)

  // Only react to an externally supplied font change.
  useEffect(() => {
    if (font && font !== selectedFont) setSelectedFont(font)
  }, [font, selectedFont])

  const activeStack = signatureFontStack(selectedFont)

  return (
    <div className={cn('space-y-3', className)}>
      <div className='space-y-1.5'>
        <Label htmlFor='typed-signature'>Your full name</Label>
        <Input
          id='typed-signature'
          value={value}
          disabled={disabled}
          maxLength={120}
          placeholder={placeholder}
          onChange={(event) => onChange({ typedText: event.target.value, typedFont: selectedFont })}
        />
      </div>

      <div className='space-y-1.5'>
        <Label>Style</Label>
        <div className='flex flex-wrap gap-2'>
          {SIGNATURE_FONTS.map((option) => (
            <button
              key={option.id}
              type='button'
              disabled={disabled}
              aria-pressed={selectedFont === option.id}
              onClick={() => {
                setSelectedFont(option.id)
                onChange({ typedText: value, typedFont: option.id })
              }}
              className={cn(
                'rounded-md border px-3 py-1.5 text-sm transition-colors',
                selectedFont === option.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
                disabled && 'opacity-60'
              )}
              style={{ fontFamily: option.stack }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className={cn('rounded-md border border-dashed p-4', SIGNATURE_SURFACE_CLASS)}>
        <p className='mb-1 text-xs text-slate-500'>Preview</p>
        <p className='min-h-[44px] text-3xl leading-tight' style={{ fontFamily: activeStack }}>
          {value || <span className={SIGNATURE_PLACEHOLDER_CLASS}>{placeholder}</span>}
        </p>
      </div>
    </div>
  )
}

export default SignatureTyped
